import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getBackendUrl } from "../utils/backendUrl";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("beauly_user")); } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem("beauly_token"));
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);

  const backendUrl = getBackendUrl();

  const fetchCredits = useCallback(async (t) => {
    const tkn = t || token;
    if (!tkn) return;
    try {
      const res = await fetch(`${backendUrl}/credits`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(data);
      }
    } catch (e) {
      console.error("Failed to fetch credits:", e);
    }
  }, [backendUrl, token]);

  // Load credits on token change
  useEffect(() => {
    if (token) fetchCredits();
  }, [token, fetchCredits]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || "Login fehlgeschlagen");
      }
      const data = await res.json();
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem("beauly_token", data.token);
      localStorage.setItem("beauly_user", JSON.stringify(data.user));
      await fetchCredits(data.token);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || "Registrierung fehlgeschlagen");
      }
      const data = await res.json();
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem("beauly_token", data.token);
      localStorage.setItem("beauly_user", JSON.stringify(data.user));
      setCredits({ credits_images: 0, credits_messages: 0 });
      return data;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCredits(null);
    localStorage.removeItem("beauly_token");
    localStorage.removeItem("beauly_user");
  };

  const deleteAccount = async () => {
    if (!token) return;
    const res = await fetch(`${backendUrl}/auth/delete-account`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Account deletion failed");
    logout();
  };

  const createCheckout = async () => {
    if (!token) return;
    const lang = localStorage.getItem("beauly_lang") || "en";
    const res = await fetch(`${backendUrl}/payments/create-checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ locale: lang }),
    });
    if (!res.ok) throw new Error("Checkout fehlgeschlagen");
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const hasCredits = credits && (credits.credits_messages > 0 || credits.credits_images > 0);
  const needsPayment = credits && !hasCredits;

  return (
    <AuthContext.Provider value={{
      user, setUser, token, credits, hasCredits, needsPayment,
      login, register, logout, deleteAccount,
      createCheckout, fetchCredits, loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
